import { FiAlertTriangle } from 'react-icons/fi';

import TextEditor from 'components/text-editor';
import { GetBehaviorLabel, GetMoedaLabel } from 'utils/strings';

import { BehaviorKey } from '../recognition';
import {
  FREE_TEXT_OPTION,
  MAX_MESSAGE_LENGTH,
  MESSAGE_PRESETS,
  MIN_MESSAGE_LENGTH,
  MessageContent,
  MessageLengthIssue,
  MessageOption,
  coinAllowsPresets,
  messageLengthIssue,
} from '../recognition-message';
import { CoinKey } from '../recognition-points';
import { ChoiceRadio, ChoiceTile, SelectedBadge } from './choice-tile';
import { StepHeading, StepPanel } from './step-layout';

const LENGTH_ISSUE_TEXT: Record<MessageLengthIssue, string> = {
  'too-short': `Para continuar, insira pelo menos ${MIN_MESSAGE_LENGTH} caracteres no campo de texto, sem contar os emojis.`,
  'too-long': `O texto inserido ultrapassou o limite máximo de ${MAX_MESSAGE_LENGTH} caracteres.`,
};

const EDITOR_LABEL = `Sua mensagem. De ${MIN_MESSAGE_LENGTH} a ${MAX_MESSAGE_LENGTH} caracteres, sem contar os emojis no mínimo.`;

const CAPTION_CLASSES = 'mt-2.5 self-center text-[11.5px] font-bold uppercase tracking-[.04em] text-brand-blue-dark';

interface MessageStepProps {
  colleagueName: string;
  coin: CoinKey;
  behavior: BehaviorKey;
  option?: MessageOption;
  /** O que a pessoa digitou. Fica guardado mesmo com uma mensagem pronta escolhida (REC-38). */
  freeText?: MessageContent;
  onSelectOption: (option: MessageOption) => void;
  onChangeFreeText: (content: MessageContent) => void;
}

type FreeTextEditorProps = Pick<MessageStepProps, 'freeText' | 'onChangeFreeText'> & {
  placeholder: string;
  /** Chamado quando o texto muda de fato, e não a cada foco ou movimento do cursor. */
  onType?: () => void;
};

function FreeTextEditor({ freeText, onChangeFreeText, placeholder, onType }: FreeTextEditorProps) {
  // O editor avisa `onChange` também ao ganhar foco e ao mover o cursor. Só o texto novo conta
  // como digitação: passar pelo campo com o Tab não pode trocar a opção já escolhida.
  const handleChange = (html: string, length = { textLength: 0, fullLength: 0 }) => {
    const typed = length.fullLength > 0 && html !== freeText?.html;

    onChangeFreeText({ html, length });
    if (typed) onType?.();
  };

  return (
    <div className="h-40 text-xs text-content">
      <TextEditor
        appearance="light"
        ariaLabel={EDITOR_LABEL}
        placeholder={placeholder}
        initialValue={freeText?.html}
        minLength={MIN_MESSAGE_LENGTH}
        maxLength={MAX_MESSAGE_LENGTH}
        onChange={handleChange}
      />
    </div>
  );
}

function MessageOptions({
  option,
  freeText,
  onSelectOption,
  onChangeFreeText,
}: Omit<MessageStepProps, 'colleagueName' | 'coin' | 'behavior'>) {
  const freeTextSelected = option === FREE_TEXT_OPTION;
  const selectFreeText = () => onSelectOption(FREE_TEXT_OPTION);

  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-3.5 border-0 p-0">
      <legend className="screen-reader-text">Mensagem do reconhecimento</legend>

      <div className="grid gap-3.5 sm:grid-cols-2">
        {MESSAGE_PRESETS.map(preset => {
          const selected = option === preset.id;

          return (
            <ChoiceTile
              key={preset.id}
              name="mensagem"
              value={String(preset.id)}
              checked={selected}
              onSelect={() => onSelectOption(preset.id)}
              className="flex-col gap-2.5 rounded-jj-md border-2 px-4 py-3.5 text-left"
              caption={
                <span aria-hidden="true" className={`${CAPTION_CLASSES} ${selected ? 'underline' : ''}`}>
                  Enviar opção {preset.id}
                </span>
              }
            >
              <span className="text-[12.5px] text-content-soft">Opção {preset.id}:</span>
              <span className="text-[13px] leading-[1.55] text-content">{preset.text}</span>
            </ChoiceTile>
          );
        })}
      </div>

      <div className="flex flex-col">
        <div
          className={`relative flex flex-col gap-2 rounded-jj-md border-2 bg-brand-white px-4 py-3.5 ${
            freeTextSelected ? 'border-brand-pink' : 'border-surface-200'
          }`}
        >
          <label className="cursor-pointer self-start">
            <ChoiceRadio name="mensagem" value={String(FREE_TEXT_OPTION)} checked={freeTextSelected} onSelect={selectFreeText} />
            <span className="text-[12.5px] text-content-soft peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-blue-dark">
              Opção {FREE_TEXT_OPTION}:<span className="screen-reader-text"> Prefiro digitar minha mensagem</span>
            </span>
          </label>

          <FreeTextEditor
            freeText={freeText}
            onChangeFreeText={onChangeFreeText}
            placeholder="Prefiro digitar minha mensagem"
            onType={selectFreeText}
          />

          {freeTextSelected && <SelectedBadge />}
        </div>

        <span aria-hidden="true" className={`${CAPTION_CLASSES} ${freeTextSelected ? 'underline' : ''}`}>
          Enviar opção {FREE_TEXT_OPTION}
        </span>
      </div>
    </fieldset>
  );
}

/** Etapa 4: a mensagem do reconhecimento (REC-33 a REC-40). */
export function MessageStep({
  colleagueName,
  coin,
  behavior,
  option,
  freeText,
  onSelectOption,
  onChangeFreeText,
}: MessageStepProps) {
  const offersPresets = coinAllowsPresets(coin);
  const writingFreeText = !offersPresets || option === FREE_TEXT_OPTION;
  const lengthIssue = writingFreeText ? messageLengthIssue(freeText?.length) : undefined;

  return (
    <div className="flex flex-col gap-3.5">
      <StepHeading>
        Agora conte a <b className="font-bold uppercase">{colleagueName}</b> o motivo de ganhar{' '}
        <span className="text-brand-blue-dark">{GetMoedaLabel(coin)}</span> em{' '}
        <span className="text-brand-blue-dark">{GetBehaviorLabel(behavior)}</span>!
      </StepHeading>

      <StepPanel>
        {offersPresets ? (
          <MessageOptions
            option={option}
            freeText={freeText}
            onSelectOption={onSelectOption}
            onChangeFreeText={onChangeFreeText}
          />
        ) : (
          <FreeTextEditor freeText={freeText} onChangeFreeText={onChangeFreeText} placeholder="Digite sua mensagem" />
        )}

        <p aria-live="polite" className="m-0 text-xs font-bold leading-normal text-content empty:hidden">
          {lengthIssue ? LENGTH_ISSUE_TEXT[lengthIssue] : null}
        </p>

        <p className="m-0 flex items-start gap-2 text-xs leading-normal text-content [text-wrap:pretty]">
          <FiAlertTriangle aria-hidden="true" size={16} className="mt-px shrink-0 text-brand-blue-dark" />
          Todos os reconhecimentos que você enviar ficarão visíveis aos outros participantes. #Ética e Integridade são as bases da
          confiança.
        </p>
      </StepPanel>
    </div>
  );
}
